import {
  AloftDate,
  AloftPrice,
  AloftTime,
  MLS,
  Tkv2MarketTrendsSeriesResult,
  assert,
  checkMinLength,
  expected,
  isDefined,
} from '@aloft/toolkit-shared';

import { CompWithSaleInfo } from '../types.js';
import {
  MarketTrendsHelperAdjustment,
  MarketTrendsHelperColumn,
  MarketTrendsHelpers,
} from './types.js';

export function getMarketTrendsHelpers(params: {
  effectiveDate: AloftDate;
  comps: CompWithSaleInfo[];
  series: Tkv2MarketTrendsSeriesResult;
  using: 'p50closeprice' | 'p50closepricepersqft';
  rounding: AloftPrice;
}): MarketTrendsHelpers {
  const {
    effectiveDate,
    comps,
    series: { data: series, meta },
    using,
    rounding,
  } = params;

  // some basic assertions on the input data. also defines 'n' as the length.
  // TODO: it would be good to check some other assumptions, e.g. that there
  // are no gaps or overlaps

  const lengths = Object.values(series)
    .filter(isDefined)
    .map((x) => x.length);
  const [n] = checkMinLength(1, lengths);
  assert(lengths.every((m) => m === n));

  // helper to find which index contains the given aloft date
  function idx(t: AloftDate | undefined): number | undefined {
    if (t === undefined) return undefined;
    for (let i = 0; i < n; i++) {
      if (expected(series.start[i]).le(t) && t.lt(expected(series.end[i]))) {
        return i;
      }
    }
    return undefined;
  }

  const compindices: string[][] = [];
  comps.forEach(({ month }, j) => {
    const i = idx(fromMonth(month));
    if (i !== undefined) (compindices[i] ??= []).push(`C${j + 1}`);
  });

  // Compute "current month" for shadowing. When an index is shadowed by
  // another index, the data at that index will come from the shadow instead.
  // Currently this is only used to copy the previous month's data to the
  // current month. Shadowing is displayed to the user as a projection.

  const today = (meta.updated ?? AloftTime.now()).toAloftDate();
  const currmonth = idx(today);
  const prevmonth = idx(today.subtractMonths(1));

  function shadow(x: number | undefined): number | undefined {
    if (x === undefined) return undefined;
    if (x === currmonth) return prevmonth;
    return x;
  }

  // At this point we can compute a portion of the result. If something goes
  // wrong after this, we can at least provide a limited amount of data.

  const updated =
    series.latestmodificationtimestamp === undefined
      ? undefined
      : series.latestmodificationtimestamp.reduce((a, b) => (a.gt(b) ? a : b));
  const sources =
    series.originatingsystemnames === undefined
      ? undefined
      : [...new Set(...series.originatingsystemnames)];
  const geo = meta.geo;
  const geophrase = `in the ${meta.geoname} (${meta.geo})`;
  const attribution = (() => {
    const parts = [
      updated?.formatTime(undefined, 'MMM dd, yyyy, hh:mm aa'),
      ...(sources?.map((x) => MLS[x].shortName) ?? []),
    ].filter(isDefined);
    return parts.length === 0 ? undefined : `Data from ${parts.join(', ')}`;
  })();

  const table: MarketTrendsHelperColumn[] = series.start.map((_, i) => {
    const j = shadow(i);
    const shadowed = j !== undefined && j !== i;
    return {
      start: expected(series.start[i]),
      end: expected(series.end[i]),
      period: expected(toMonth(series.start[i])),
      numcloses: shadowed ? undefined : (series.numcloses?.[i] ?? undefined),
      p50closeprice: shadowed
        ? (series.p50closeprice?.[j] ?? undefined)
        : (series.p50closeprice?.[i] ?? undefined),
      p50closepriceoverlistprice: shadowed
        ? undefined
        : (series.p50closepriceoverlistprice?.[i] ?? undefined),
      p50closepricepersqft: shadowed ? undefined : (series.p50closepricepersqft?.[i] ?? undefined),
      p50daysonmarketclosed: shadowed
        ? undefined
        : (series.p50daysonmarketclosed?.[i] ?? undefined),
      p50listprice: shadowed ? undefined : (series.p50listprice?.[i] ?? undefined),
      comps: compindices[i] ?? [],
      shadowed,
    };
  });

  table.sort((a, b) => a.start.compare(b.start));
  const fields = { updated, sources, geo, geophrase, attribution, table };

  // With the table and additional data constructed, we perform some final
  // checks before building the adjustment logic itself.

  const i1 = shadow(idx(effectiveDate));
  const market_ = series[using];
  if (i1 === undefined) {
    return { warning: 'Effective date does not appear in the data', ...fields };
  }
  if (market_ === undefined) {
    return { warning: `No trend for ${using}`, ...fields };
  }
  const m1_ = market_[i1] ?? undefined;
  if (m1_ === undefined) {
    return { warning: `No trend data for effective date`, ...fields };
  }

  // stupid typescript hack, to have narrowed types inside 'adjust':
  const market = market_;
  const m1 = m1_;

  // The actual adjustment logic.

  // We assume P(t1)/P(t0) = M(t1)/M(t0), where M(t) and P(t) are the market
  // trend and comp value respectively at time t. To make a time adjustment,
  // we take t1 to be the effective date and t0 to be the comp's close date,
  // then solve for P(t1), i.e. compute P(t1) = P(t0) * M(t1)/M(t0). The
  // difference between P(t1) and P(t0) is presented as both a dollar amount,
  // and as the value x such that P(t1) = P(t0)*(1 + x), which is just
  // M(t1)/M(t0) - 1

  function adjust(
    t0: AloftDate,
    p0: AloftPrice | undefined,
  ): MarketTrendsHelperAdjustment | undefined {
    const i0 = shadow(idx(t0));
    if (i0 === undefined) return undefined;

    const t1 = effectiveDate;
    const m0 = expected(market[i0]);

    const trueratio = m1.toDollarLike() / m0.toDollarLike();
    const adjpercent = Math.round((trueratio - 1) / 0.001) * 0.001;
    const ratio = adjpercent + 1;

    if (p0 === undefined) return { t0, t1, adjpercent };

    const p1 = p0.mul(ratio);
    const adjprice = p1.sub(p0).roundTo(rounding);

    return { t0, t1, p0, p1, adjpercent, adjprice };
  }

  for (const col of table) {
    col.adjpercent = adjust(col.start, undefined)?.adjpercent;
  }

  return { ...fields, adjust, adjref: m1 };
}

function toMonth(x: AloftDate | AloftTime): string;
function toMonth(x: AloftDate | AloftTime | undefined): string | undefined;
function toMonth(x: AloftDate | AloftTime | undefined): string | undefined {
  if (x === undefined) return undefined;
  if (AloftDate.isAloftDate(x)) return x.format('yyyy-MM');
  if (AloftTime.isAloftTime(x)) return x.toAloftDate().format('yyyy-MM');
  throw new Error(`unsure what to do with ${x}`);
}

function fromMonth(x: string): AloftDate;
function fromMonth(x: string | undefined): AloftDate | undefined;
function fromMonth(x: string | undefined): AloftDate | undefined {
  if (x === undefined) return undefined;
  return AloftDate.fromIsoString(`${x}-01`);
}
